
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Maximize2, Play, Pause, GripHorizontal } from 'lucide-react';
import { useVideo } from '../../context/VideoContext';
import { useYouTubeAPI } from '../../hooks/useYouTubePlayer';

interface FloatingPlayerProps {
    onReturnToMatch: (matchId: string) => void;
}

interface Position {
    x: number;
    y: number;
}

export const FloatingPlayer: React.FC<FloatingPlayerProps> = ({ onReturnToMatch }) => {
    const { activeVideo, setActiveVideo } = useVideo();
    const apiReady = useYouTubeAPI();
    const playerRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const floatingRef = useRef<HTMLDivElement>(null);
    const timeTrackerRef = useRef<number | null>(null);

    // Position State
    const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStartPos = useRef<Position>({ x: 0, y: 0 });
    const initialPos = useRef<Position>({ x: 0, y: 0 });

    const handleMouseDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
        setIsDragging(true);
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        
        dragStartPos.current = { x: clientX, y: clientY };
        initialPos.current = { x: position.x, y: position.y };
        
        // Prevent text selection during drag
        document.body.style.userSelect = 'none';
    }, [position]);

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent | TouchEvent) => {
            const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
            const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;

            const deltaX = clientX - dragStartPos.current.x;
            const deltaY = clientY - dragStartPos.current.y;

            setPosition({ 
                x: initialPos.current.x + deltaX, 
                y: initialPos.current.y + deltaY 
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            document.body.style.userSelect = '';

            // Snap back within boundaries if out of bounds
            const rect = floatingRef.current?.getBoundingClientRect();
            if (rect) {
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const margin = 16; // Margin from edges

                // The element is 'fixed' with bottom-24 md:bottom-8 and right-4
                // We need to calculate the limits based on its initial fixed position
                // But a simpler way is to check the current bounding rect vs viewport
                
                let snapX = 0;
                let snapY = 0;

                // Check horizontal bounds
                if (rect.left < margin) {
                    snapX = margin - rect.left;
                } else if (rect.right > viewportWidth - margin) {
                    snapX = (viewportWidth - margin) - rect.right;
                }

                // Check vertical bounds
                if (rect.top < margin) {
                    snapY = margin - rect.top;
                } else if (rect.bottom > viewportHeight - margin) {
                    snapY = (viewportHeight - margin) - rect.bottom;
                }

                if (snapX !== 0 || snapY !== 0) {
                    setPosition(prev => ({
                        x: prev.x + snapX,
                        y: prev.y + snapY
                    }));
                }
            }
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('touchmove', handleMouseMove, { passive: false });
        window.addEventListener('touchend', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleMouseMove);
            window.removeEventListener('touchend', handleMouseUp);
        };
    }, [isDragging]);

    // Initialize YouTube Player
    useEffect(() => {
        if (!apiReady || !containerRef.current || !window.YT || !activeVideo) return;

        playerRef.current = new window.YT.Player(containerRef.current, {
            videoId: activeVideo.videoId,
            height: '100%',
            width: '100%',
            playerVars: {
                autoplay: 1,
                mute: 1,
                controls: 0,
                modestbranding: 1,
                rel: 0,
                start: Math.floor(activeVideo.currentTime || 0)
            },
            events: {
                onReady: (event: any) => {
                    if (activeVideo.isPaused) {
                        event.target.pauseVideo();
                    } else {
                        event.target.playVideo();
                    }
                    
                    // Start tracking time
                    timeTrackerRef.current = window.setInterval(() => {
                        const playerState = event.target.getPlayerState();
                        if (playerState === window.YT.PlayerState.PLAYING) {
                            const time = event.target.getCurrentTime();
                            setActiveVideo(prev => {
                                if (!prev || prev.videoId !== activeVideo.videoId) return prev;
                                return { ...prev, currentTime: time, isPaused: false };
                            });
                        }
                    }, 1000);
                },
                onStateChange: (event: any) => {
                    // Sync play/pause state back to context
                    if (event.data === window.YT.PlayerState.PAUSED) {
                        setActiveVideo({ ...activeVideo, isPaused: true });
                    } else if (event.data === window.YT.PlayerState.PLAYING) {
                        setActiveVideo({ ...activeVideo, isPaused: false });
                    }
                }
            }
        });

        return () => {
            if (timeTrackerRef.current) clearInterval(timeTrackerRef.current);
            if (playerRef.current?.destroy) playerRef.current.destroy();
        };
    }, [apiReady, activeVideo?.videoId]);

    // React to external pause/play signals (e.g. from Shorts)
    useEffect(() => {
        if (playerRef.current && playerRef.current.getPlayerState) {
            const state = playerRef.current.getPlayerState();
            if (activeVideo?.isPaused && state !== window.YT.PlayerState.PAUSED) {
                playerRef.current.pauseVideo();
            } else if (!activeVideo?.isPaused && state === window.YT.PlayerState.PAUSED) {
                playerRef.current.playVideo();
            }
        }
    }, [activeVideo?.isPaused]);

    if (!activeVideo) return null;

    return (
        <div 
            ref={floatingRef}
            style={{
                transform: `translate(${position.x}px, ${position.y}px)`,
                transition: isDragging ? 'none' : 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
            className="fixed bottom-24 md:bottom-8 right-4 z-[100] w-64 md:w-80 aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl border border-gray-100/10 animate-in slide-in-from-bottom-4 duration-500"
        >
            <div className="absolute inset-0 bg-slate-900 flex items-center justify-center pointer-events-none">
                <Play className="text-white/20 animate-pulse" size={32} />
            </div>
            
            <div ref={containerRef} className="w-full h-full relative z-10" />

            {/* Overlay Controls */}
            <div className="absolute inset-0 z-20 flex flex-col justify-between p-3 md:opacity-0 md:hover:opacity-100 transition-opacity md:bg-black/40 md:backdrop-blur-[1px]">
                {/* Drag Handle */}
                <div 
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleMouseDown}
                    className="absolute inset-0 cursor-move z-0"
                    title="Drag to move"
                />

                <div className="flex justify-between items-start relative z-10 pointer-events-none">
                    <div className="flex-1 pr-4">
                        <div className="flex items-center gap-1 mb-1">
                            <GripHorizontal size={10} className="text-white/40" />
                            <h4 className="text-[10px] font-black text-white line-clamp-1 uppercase tracking-tight leading-none">Now Watching</h4>
                        </div>
                        <p className="text-[11px] font-bold text-white/80 line-clamp-1">{activeVideo.title}</p>
                    </div>
                    <button 
                        onClick={() => setActiveVideo(null)}
                        className="w-7 h-7 flex items-center justify-center bg-black/60 rounded-full text-white md:hover:bg-rose-500 transition-colors pointer-events-auto"
                    >
                        <X size={14} strokeWidth={3} />
                    </button>
                </div>
                
                <div className="flex justify-between items-center relative z-10 pointer-events-none">
                    {/* <button 
                        onClick={() => setActiveVideo({ ...activeVideo, isPaused: !activeVideo.isPaused })}
                        className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full text-white md:hover:bg-white/40 transition-all"
                    >
                        {activeVideo.isPaused ? <Play size={14} fill="white" /> : <Pause size={14} fill="white" />}
                    </button> */}
                    
                    {activeVideo.matchId && (
                        <button 
                            onClick={() => onReturnToMatch(activeVideo.matchId!)}
                            className="bg-white text-slate-900 p-2 rounded-xl flex items-center gap-1.5 shadow-lg active:scale-95 transition-all pointer-events-auto"
                        >
                            <Maximize2 size={12} strokeWidth={3} />
                            <span className="text-[10px] font-black uppercase tracking-widest">Restore</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
